import { computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';

export const usePermissions = () => {
  const authStore = useAuthStore();

  const permissionSet = computed<Set<string>>(() => new Set(authStore.user?.permissions ?? []));
  const userId = computed(() => authStore.user?.id ?? null);

  const cache = new Map<string, boolean>();

  watch([permissionSet, userId], () => {
    cache.clear();
  });

  const hasExactPermission = (permission: string): boolean => {
    return permissionSet.value.has(permission);
  };

  const resolvePermission = (action: string, resourceOwnerId?: string | null): boolean => {
    if (action.endsWith('.all')) {
      return hasExactPermission(action);
    }

    if (action.endsWith('.own')) {
      const isOwner = Boolean(resourceOwnerId) && resourceOwnerId === userId.value;
      return isOwner && hasExactPermission(action);
    }

    if (hasExactPermission(action)) {
      return true;
    }

    if (hasExactPermission(`${action}.all`)) {
      return true;
    }

    const isOwner = Boolean(resourceOwnerId) && resourceOwnerId === userId.value;
    return isOwner && hasExactPermission(`${action}.own`);
  };

  const can = (action: string, resourceOwnerId?: string | null): boolean => {
    const shouldUseCache = !resourceOwnerId && !action.endsWith('.own');

    if (shouldUseCache) {
      const cacheKey = `${userId.value ?? 'anonymous'}:${action}`;
      const cached = cache.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }

      const result = resolvePermission(action, resourceOwnerId);
      cache.set(cacheKey, result);
      return result;
    }

    return resolvePermission(action, resourceOwnerId);
  };

  const canAny = (permissions: readonly string[]): boolean => {
    return permissions.some(permission => can(permission));
  };

  const hasAnyExact = (permissions: readonly string[]): boolean => {
    return permissions.some(permission => hasExactPermission(permission));
  };

  const canAll = (permissions: readonly string[]): boolean => {
    return permissions.every(permission => can(permission));
  };

  return {
    can,
    canAny,
    hasAnyExact,
    canAll,
  };
};
